import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Sprout,
  Heart,
  TrendingUp,
  ArrowUpRight,
  Check,
  ExternalLink,
} from "lucide-react";
import { fundingPaths, LMI_FUNDING_URL } from "../mock";

const iconMap = { Sprout, Heart, TrendingUp };

const FundingSection = () => {
  return (
    <section id="funding" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Funding for Kingdom-minded businesses
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-black leading-[1.05]">
            Bring the calling.{" "}
            <em className="text-teal-700 font-serif font-normal">
              We&apos;ll bring the capital.
            </em>
          </h2>
          <p className="mt-5 text-lg text-black/60 max-w-2xl">
            Three funding paths for believers stepping out in faith. Powered
            by our partner{" "}
            <strong className="text-black">
              LMI Marketing & Business Solutions Group, Inc.
            </strong>
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7">
          {fundingPaths.map((p, i) => {
            const Icon = iconMap[p.icon];
            const isFeatured = i === 0;
            return (
              <article
                key={p.id}
                className={`relative rounded-2xl p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 ${
                  isFeatured
                    ? "bg-black text-white border border-teal-400/40 shadow-[0_25px_60px_-25px_rgba(20,184,166,0.5)]"
                    : "bg-white text-black border border-black/10 hover:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.2)]"
                }`}
              >
                {p.badge && (
                  <span className="absolute -top-3 left-7 bg-teal-400 text-black text-[11px] font-semibold px-3 py-1 rounded-full tracking-wide uppercase">
                    {p.badge}
                  </span>
                )}
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isFeatured
                      ? "bg-teal-400 text-black"
                      : "bg-teal-500/10 text-teal-700 border border-teal-600/20"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold tracking-tight">
                  {p.title}
                </h3>
                <div
                  className={`mt-1.5 text-sm font-medium ${
                    isFeatured ? "text-teal-300" : "text-teal-700"
                  }`}
                >
                  {p.audience}
                </div>
                <div
                  className={`mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-wide font-mono ${
                    isFeatured ? "text-white/70" : "text-black/60"
                  }`}
                >
                  <span>Range</span>
                  <span
                    className={`font-semibold ${
                      isFeatured ? "text-white" : "text-black"
                    }`}
                  >
                    {p.range}
                  </span>
                </div>
                <p
                  className={`mt-4 leading-relaxed ${
                    isFeatured ? "text-white/70" : "text-black/60"
                  }`}
                >
                  {p.summary}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-2.5 text-sm ${
                        isFeatured ? "text-white/85" : "text-black/70"
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                          isFeatured
                            ? "bg-teal-400 text-black"
                            : "bg-teal-500/15 text-teal-700"
                        }`}
                      >
                        <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/funding" className="block mt-7">
                  <Button
                    className={`w-full font-semibold h-11 ${
                      isFeatured
                        ? "bg-teal-400 hover:bg-teal-300 text-black"
                        : "bg-black hover:bg-black/85 text-white"
                    }`}
                  >
                    Learn more
                    <ArrowUpRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              </article>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl bg-[#f6faf9] border border-black/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.15em] text-teal-700 font-medium">
              Ready to apply?
            </div>
            <div className="mt-1.5 text-xl md:text-2xl font-semibold text-black">
              Begin your funding intake.
            </div>
            <div className="mt-2 text-black/55 text-sm">
              Our partner LMI Marketing & Business Solutions Group is launching
              shortly. Until then, complete a short interim form here and
              we&apos;ll personally route you through prequalification.
            </div>
          </div>
          <Link to={LMI_FUNDING_URL} className="shrink-0">
            <Button className="bg-black hover:bg-teal-700 text-white font-semibold h-12 px-6">
              Start funding intake
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FundingSection;
